import snakeCase from 'lodash/snakeCase'
import {
  internalSearchButtonSchema,
  internalSearchBrandSchema,
  internalSearchImageSchema,
  internalSearchFilterSchema,
  internalSearchExpandedSchema,
} from '@integration-layer/schemas/GAEvents/internalSearchEvents'

// Enable debug mode based on environment variable
const debug = !!process.env.DEBUG_GA_EVENTS

/**
 * Provides methods for tracking various internal search events in Google Analytics.
 *
 * @returns {Object} An object containing methods to dispatch different internal search events.
 *
 * @description This composable handles tracking of internal search interactions,
 * including button clicks, brand searches, image searches, filters, and search expansions.
 *
 * @example
 * const {
 *   dispatchInternalSearchButton,
 *   dispatchInternalSearchBrand
 * } = useGAInternalSearchEvents()
 *
 * dispatchInternalSearchButton()
 * dispatchInternalSearchBrand('shirt', 'Armani')
 */
export const useGAInternalSearchEvents = () => {
  /**
   * Tracks the click event on internal search button.
   *
   * @param {'-' | 'pdp'} eventLabel - Entry point of the search: '-' for the header, 'pdp' for the PDP bottom search bar.
   *
   * @description Sends a Google Analytics event when the internal search button is clicked.
   */
  const dispatchInternalSearchButton = (eventLabel: '-' | 'pdp' = '-') => {
    useGASendEvent(
      internalSearchButtonSchema,
      {
        event: 'GAevent',
        eventID: '013',
        eventCategory: 'internal_search',
        eventAction: 'click_cta',
        eventLabel,
        eventElement01: '-',
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Tracks brand-specific internal search events.
   *
   * @param {string} query - The search query entered by the user.
   * @param {string} brand - The brand being searched.
   *
   * @description Sends a Google Analytics event for brand-specific searches,
   * with special handling for the EA7 brand.
   */
  const dispatchInternalSearchBrand = (query: string, brand: string) => {
    useGASendEvent(
      internalSearchBrandSchema,
      {
        event: 'GAevent',
        eventID: '029',
        eventCategory: 'internal_search',
        eventAction: 'click_cta',
        eventLabel: 'search_brand',
        eventElement01: query,
        eventElement02: snakeCase(brand) === 'ea_7' ? 'EA7' : snakeCase(brand),
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Tracks image search events.
   *
   * @param {string} query - The search query used for the image search.
   *
   * @description Sends a Google Analytics event when an image search is performed.
   */
  const dispatchInternalSearchImage = (query: string) => {
    useGASendEvent(
      internalSearchImageSchema,
      {
        event: 'GAevent',
        eventID: '030',
        eventCategory: 'internal_search',
        eventAction: 'click_cta',
        eventLabel: 'search_image',
        eventElement01: query,
        eventElement02: '-',
        eventElement03: '-',
      },
      { debug }
    )
  }

  /**
   * Tracks internal search filter events.
   *
   * @param {string} query - The original search query.
   * @param {string} filter - The filter applied to the search.
   * @param {string} collection - The collection being filtered.
   *
   * @description Sends a Google Analytics event when search filters are applied.
   */
  const dispatchInternalFilter = (
    query: string,
    filter: string,
    collection: string
  ) => {
    useGASendEvent(
      internalSearchFilterSchema,
      {
        event: 'GAevent',
        eventID: '031',
        eventCategory: 'internal_search',
        eventAction: 'click_cta',
        eventLabel: 'internal_filter',
        eventElement01: query,
        eventElement02: snakeCase(filter),
        eventElement03: snakeCase(collection),
      },
      { debug }
    )
  }

  /**
   * Tracks internal search expansion events.
   *
   * @param {string} query - The search query being expanded.
   * @param {string} brand - The brand associated with the search expansion.
   *
   * @description Sends a Google Analytics event when search results are expanded,
   * with special handling for the EA7 brand.
   */
  const dispatchInternalSearchExpansion = (query: string, brand: string) => {
    useGASendEvent(
      internalSearchExpandedSchema,
      {
        event: 'GAevent',
        eventID: '032',
        eventCategory: 'internal_search',
        eventAction: 'click_cta',
        eventLabel: 'internal_expansion',
        eventElement01: query,
        eventElement02: snakeCase(brand) === 'ea_7' ? 'EA7' : snakeCase(brand),
        eventElement03: '-',
      },
      { debug }
    )
  }

  return {
    dispatchInternalSearchButton,
    dispatchInternalSearchBrand,
    dispatchInternalSearchImage,
    dispatchInternalFilter,
    dispatchInternalSearchExpansion,
  }
}
